import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { sortNovelIdeaTags } from '@/lib/novelIdeaTags'
import { Loader2, Search, Check, Lightbulb } from 'lucide-react'
import type { Idea } from '@/types'
import { cn, filterValidMediaUrls } from '@/lib/utils'
import { prioritizeIdeasByFormat } from '@/lib/ideaFormat'
import type { IdeaAdFormat } from '@/lib/ideaFormat'

interface ImportIdeaModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (ideas: Idea[]) => void
  maxSelection?: number
  excludeIds?: string[]
  preferredAdFormat?: IdeaAdFormat
  defaultProductId?: string
  defaultTag?: string
}

export function ImportIdeaModal({
  open,
  onClose,
  onConfirm,
  maxSelection = 10,
  excludeIds = [],
  preferredAdFormat,
  defaultProductId,
  defaultTag,
}: ImportIdeaModalProps) {
  const [productId, setProductId] = useState<string>('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTag, setSelectedTag] = useState('all')

  useEffect(() => {
    if (!open) return
    setProductId(defaultProductId || '')
    setSelectedIds(new Set())
    setSearchQuery('')
    setSelectedTag(defaultTag || 'all')
  }, [defaultProductId, defaultTag, open])

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
    enabled: open,
  })

  const { data: ideasData, isLoading } = useQuery({
    queryKey: ['ideas', productId, 'import'],
    queryFn: () => api.getIdeas(productId, { limit: 100, sort: 'unused_first' }),
    enabled: open && !!productId,
  })

  const importableIdeas = useMemo(
    () => (ideasData?.items || []).filter((idea) => !excludeIds.includes(idea.id)),
    [excludeIds, ideasData?.items]
  )

  const availableTags = useMemo(
    () => sortNovelIdeaTags(importableIdeas.flatMap((idea) => idea.tags || [])),
    [importableIdeas]
  )

  useEffect(() => {
    if (selectedTag === 'all') return
    if (availableTags.includes(selectedTag)) return
    setSelectedTag(defaultTag && availableTags.includes(defaultTag) ? defaultTag : 'all')
  }, [availableTags, defaultTag, selectedTag])

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredIdeas = importableIdeas.filter((idea) => {
    const matchesTag = selectedTag === 'all' || idea.tags.includes(selectedTag)
    if (!matchesTag) return false
    if (!normalizedSearchQuery) return true
    return (
      idea.title.toLowerCase().includes(normalizedSearchQuery) ||
      idea.description.toLowerCase().includes(normalizedSearchQuery) ||
      idea.tags.some((tag) => tag.toLowerCase().includes(normalizedSearchQuery))
    )
  })
  const ideas = preferredAdFormat
    ? prioritizeIdeasByFormat(filteredIdeas, preferredAdFormat)
    : filteredIdeas

  const toggleIdea = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else if (next.size < maxSelection) {
        next.add(id)
      }
      return next
    })
  }

  const handleConfirm = () => {
    const selectedIdeas = importableIdeas.filter((idea) => selectedIds.has(idea.id))
    onConfirm(selectedIdeas)
    setSelectedIds(new Set())
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Import Ideas</h2>
          <p className="text-sm text-gray-500 mt-1">
            Select ideas to generate from ({selectedIds.size}/{maxSelection} selected)
          </p>
        </div>

        {/* Product Selector + Filters */}
        <div className="px-6 py-3 border-b border-gray-100 space-y-3">
          <select
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value)
              setSelectedIds(new Set())
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            <option value="">Select a product...</option>
            {products?.items?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.product_name}
              </option>
            ))}
          </select>

          {productId && (
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search ideas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">All tags</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Ideas List */}
        <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
          {!productId ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Lightbulb className="h-10 w-10 mb-3" />
              <p className="text-sm">Select a product to see its ideas</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : ideas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Lightbulb className="h-10 w-10 mb-3" />
              <p className="text-sm">No ideas found for this product</p>
            </div>
          ) : (
            <div className="space-y-2">
              {ideas.map((idea) => (
                <button
                  key={idea.id}
                  onClick={() => toggleIdea(idea.id)}
                  className={cn(
                    'w-full text-left p-3 rounded-lg border transition-colors',
                    selectedIds.has(idea.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                        selectedIds.has(idea.id)
                          ? 'bg-primary border-primary text-white'
                          : 'border-gray-300'
                      )}
                    >
                      {selectedIds.has(idea.id) && <Check className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{idea.title}</p>
                        {idea.used_count > 0 && (
                          <span className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            Used {idea.used_count}x
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{idea.description}</p>
                      {idea.tags.length > 0 && (
                        <div className="flex gap-1 mt-1.5">
                          {idea.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {(() => {
                      const previewMediaUrl = filterValidMediaUrls(idea.media_urls)[0]
                      if (!previewMediaUrl) return null

                      return (
                        <img
                          src={previewMediaUrl}
                          alt=""
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                      )
                    })()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedIds.size === 0}>
            Import {selectedIds.size} Idea{selectedIds.size !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  )
}
